import React, { useEffect, useState } from 'react';
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButton,
  IonButtons,
  IonIcon,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardSubtitle,
  IonCardContent,
  IonBadge,
  IonFab,
  IonFabButton,
  IonSpinner,
  IonAvatar,
  IonActionSheet,
  IonAlert,
  IonToast,
  IonSelect,
  IonSelectOption,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonAccordion,
  IonAccordionGroup,
  IonItem,
  IonLabel,
  useIonViewWillEnter
} from '@ionic/react';
import {
  add,
  logOutOutline,
  trashOutline,
  createOutline,
  closeOutline,
  checkmarkCircle,
  calendarOutline,
  documentTextOutline,
  cloudDoneOutline
} from 'ionicons/icons';
import { signOut } from '../services/authService';
import { getUserEvents, deleteDraftEvent, updateEventStatus, getEventStatusLabel, getEventStatusColor } from '../services/eventsService';
import { redeemToken } from '../services/tokenService';
import TokenInputModal from '../components/TokenInputModal';
import { getPaymentOptions } from '../services/pricingService';
import { createPayment } from '../services/paymentService';
import { Event, EventStatus } from '../types/event.types';
import { PaymentOption } from '../types/payment.types';
import { useHistory } from 'react-router-dom';
import { auth } from '../config/firebase';
import { getAppConfig, AppConfig } from '../services/configService';
import { useTranslation } from '../hooks/useTranslation';
import ConfirmDialog from '../components/ConfirmDialog';
import './DashboardPage.css';

const PAGE_SIZE = 10;

const isEventValid = (date: any): boolean => {
  if (!date) return false;
  const eventDate = date.toDate ? date.toDate() : new Date(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return eventDate >= today;
};

type EventSection = 'pending' | 'valid' | 'expired';
type TaggedEvent = Event & { _section: EventSection };

const DashboardPage: React.FC = () => {
  const history = useHistory();
  const user = auth.currentUser;
  const { t } = useTranslation();

  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastColor, setToastColor] = useState<'warning' | 'danger'>('warning');
  const [appConfig, setAppConfig] = useState<AppConfig>({ enableCharging: false });

  const [tokenEvent, setTokenEvent] = useState<Event | null>(null);

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [publishingEvent, setPublishingEvent] = useState<Event | null>(null);
  const [paymentOptions, setPaymentOptions] = useState<PaymentOption[]>([]);
  const [selectedPayment, setSelectedPayment] = useState<PaymentOption | null>(null);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    loadEvents();
    getAppConfig().then(setAppConfig).catch(console.error);
  }, []);

  useIonViewWillEnter(() => {
    loadEvents();
  });

  const loadEvents = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const userEvents = await getUserEvents(user.uid);
      setEvents(userEvents);
      setDisplayCount(PAGE_SIZE);
    } catch (error) {
      console.error('Error cargando eventos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    history.replace('/login');
  };

  const handleLogoutClick = () => setShowLogoutConfirm(true);

  const handleCreateEvent = () => {
    if (appConfig.enableCharging) {
      const pending = events.filter(e => e.status === 2);
      if (pending.length >= 1) {
        setToastMessage(t('dashboard.draft_limit'));
        setToastColor('warning');
        setShowToast(true);
        return;
      }
    }
    history.push('/create-event');
  };

  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
    setShowActionSheet(true);
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;
    try {
      await deleteDraftEvent(selectedEvent.id);
      setEvents(events.filter(e => e.id !== selectedEvent.id));
      setSelectedEvent(null);
    } catch (error) {
      setToastMessage(t('dashboard.delete_error'));
      setToastColor('danger');
      setShowToast(true);
    }
  };

  const handleOpenPaymentModal = async (event: Event) => {
    setPublishingEvent(event);
    setShowPaymentModal(true);
    setLoadingPricing(true);
    try {
      const options = await getPaymentOptions();
      setPaymentOptions(options);
      if (options.length > 0) setSelectedPayment(options[0]);
    } catch (error) {
      console.error('Error cargando opciones de pago:', error);
    } finally {
      setLoadingPricing(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!publishingEvent || !selectedPayment || !user) return;
    setProcessingPayment(true);
    try {
      await createPayment({
        eventId: publishingEvent.id,
        userId: user.uid,
        amount: selectedPayment.price,
        currency: selectedPayment.id,
        paypalLink: selectedPayment.paypalLink || ''
      });
      await updateEventStatus(publishingEvent.id, EventStatus.PUBLISHED);
      const paypalUrl = `https://www.paypal.com/ncp/payment/${selectedPayment.paypalLink}`;
      window.open(paypalUrl, '_blank');
      setShowPaymentModal(false);
      setPublishingEvent(null);
      setToastMessage(t('dashboard.payment_success'));
      setToastColor('warning');
      setShowToast(true);
      await loadEvents();
    } catch (error) {
      console.error('Error procesando pago:', error);
      setToastMessage(t('dashboard.payment_error'));
      setToastColor('danger');
      setShowToast(true);
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleEditEvent = () => {
    if (!selectedEvent) return;
    history.push(`/edit-event/${selectedEvent.id}`);
    setShowActionSheet(false);
  };

  const getAvatarUrl = () => {
    if (!user?.photoURL) {
      const name = user?.displayName || user?.email || 'Usuario';
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=2271b1&color=fff&size=200&bold=true`;
    }
    return user.photoURL;
  };

  const handleInfiniteScroll = (ev: CustomEvent) => {
    setTimeout(() => {
      setDisplayCount(prev => prev + PAGE_SIZE);
      (ev.target as HTMLIonInfiniteScrollElement).complete();
    }, 400);
  };

  // Grupos: pendientes = no publicados aún, aprobados = ya publicados
  const pending = events.filter(e => !e.published);
  const approved = events.filter(e => e.published);


  return (
    <IonPage>
      <IonHeader>
        <IonToolbar className="cms-toolbar">
          <IonTitle>{t('dashboard.title')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleLogoutClick} className="cms-logout-btn">
              <IonIcon icon={logOutOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="dashboard-content">
        {/* User Info */}
        <div className="user-header">
          <IonAvatar className="user-avatar">
            <img
              src={getAvatarUrl()}
              alt="Avatar"
              onError={(e) => {
                const name = user?.displayName || user?.email || 'Usuario';
                e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=2271b1&color=fff&size=200&bold=true`;
              }}
            />
          </IonAvatar>
          <div className="user-info">
            <h2>{t('dashboard.welcome', { name: user?.displayName || 'Usuario' })}</h2>
            <p>{t('dashboard.event_admin')}</p>
          </div>
        </div>

        {/* Stats Bar */}
        {!loading && events.length > 0 && (
          <div className="stats-bar">
            <div className="stat-card">
              <IonIcon icon={calendarOutline} className="stat-icon" />
              <div className="stat-info">
                <span className="stat-number">{events.length}</span>
                <span className="stat-label">{t('dashboard.total_events')}</span>
              </div>
            </div>
            <div className="stat-card">
              <IonIcon icon={documentTextOutline} className="stat-icon stat-icon-draft" />
              <div className="stat-info">
                <span className="stat-number">{pending.length}</span>
                <span className="stat-label">{t('dashboard.pending')}</span>
              </div>
            </div>
            <div className="stat-card">
              <IonIcon icon={cloudDoneOutline} className="stat-icon stat-icon-published" />
              <div className="stat-info">
                <span className="stat-number">{approved.length}</span>
                <span className="stat-label">{t('dashboard.published')}</span>
              </div>
            </div>
          </div>
        )}

        <div style={{ padding: '0 10%' }}>
        {loading ? (
          <div className="loading-container">
            <IonSpinner name="crescent" />
            <p>{t('dashboard.loading_events')}</p>
          </div>
        ) : events.length === 0 ? (
          <div className="empty-state">
            <IonIcon icon={calendarOutline} className="empty-icon" />
            <h2>{t('dashboard.no_events_title')}</h2>
            <p>{t('dashboard.no_events_desc')}</p>
            <p>{t('dashboard.no_events_hint')}</p>
            <IonButton onClick={handleCreateEvent} className="cms-primary-btn">
              {t('dashboard.create_event')}
            </IonButton>
          </div>
        ) : (
          <IonAccordionGroup multiple={true} value={['pending', 'approved']}>

            {/* Pendientes */}
            {pending.length > 0 && (
              <IonAccordion value="pending" className="dashboard-accordion">
                <IonItem slot="header" lines="none" className="dashboard-accordion-header">
                  <IonLabel className="dashboard-accordion-label">
                    {t('dashboard.pending_section', { count: String(pending.length) })}
                  </IonLabel>
                </IonItem>
                <div slot="content" className="dashboard-accordion-content">
                  {pending.map(event => (
                    <EventCard key={event.id} event={event} onClick={() => handleEventClick(event)} t={t} />
                  ))}
                </div>
              </IonAccordion>
            )}

            {/* Aprobados */}
            {approved.length > 0 && (
              <IonAccordion value="approved" className="dashboard-accordion">
                <IonItem slot="header" lines="none" className="dashboard-accordion-header dashboard-accordion-header--approved">
                  <IonLabel className="dashboard-accordion-label">
                    {t('dashboard.published_section', { count: String(approved.length) })}
                  </IonLabel>
                </IonItem>
                <div slot="content" className="dashboard-accordion-content">
                  {approved.map(event => (
                    <EventCard key={event.id} event={event} onClick={() => handleEventClick(event)} t={t} />
                  ))}
                </div>
              </IonAccordion>
            )}

          </IonAccordionGroup>
        )}

        {/* Action Sheet */}
        <IonActionSheet
          isOpen={showActionSheet}
          onDidDismiss={() => setShowActionSheet(false)}
          header={selectedEvent?.information}
          buttons={[
            ...(selectedEvent?.status === 5 ? [{
              text: t('token.enter_code'),
              icon: checkmarkCircle,
              handler: () => { setTokenEvent(selectedEvent); setShowActionSheet(false); }
            }] : []),
            {
              text: t('common.edit'),
              icon: createOutline,
              handler: handleEditEvent
            },
            {
              text: t('common.delete'),
              icon: trashOutline,
              role: 'destructive' as const,
              handler: () => setShowDeleteAlert(true)
            },
            {
              text: t('common.cancel'),
              role: 'cancel' as const
            }
          ]}
        />

        <IonAlert
          isOpen={showDeleteAlert}
          onDidDismiss={() => setShowDeleteAlert(false)}
          header={t('dashboard.delete_event')}
          message={t('dashboard.delete_confirm', { name: selectedEvent?.information || '' })}
          buttons={[
            { text: t('common.cancel'), role: 'cancel' },
            { text: t('common.delete'), role: 'destructive', handler: handleDeleteEvent }
          ]}
        />

        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          color={toastColor}
          position="top"
        />

        {/* Token Input Modal */}
        {tokenEvent && user && (
          <TokenInputModal
            eventId={tokenEvent.id}
            eventName={tokenEvent.information}
            userId={user.uid}
            onRedeem={redeemToken}
            onSuccess={() => { setTokenEvent(null); loadEvents(); }}
            onClose={() => setTokenEvent(null)}
          />
        )}

        {/* Payment Modal */}
        {showPaymentModal && (
          <div className="payment-modal-overlay" onClick={() => !processingPayment && setShowPaymentModal(false)}>
            <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
              <div className="payment-modal-header">
                <h2>{t('dashboard.publish_event')}</h2>
                <button
                  className="payment-modal-close"
                  onClick={() => !processingPayment && setShowPaymentModal(false)}
                >
                  <IonIcon icon={closeOutline} />
                </button>
              </div>

              {loadingPricing ? (
                <div className="payment-modal-loading">
                  <IonSpinner name="crescent" color="primary" />
                  <p>{t('dashboard.loading_payment')}</p>
                </div>
              ) : selectedPayment ? (
                <div className="payment-modal-body">
                  <p className="payment-modal-event-name">{publishingEvent?.information}</p>
                  <div className="payment-modal-selector">
                    <IonSelect
                      value={selectedPayment.id}
                      onIonChange={(e) => {
                        const opt = paymentOptions.find(o => o.id === e.detail.value);
                        if (opt) setSelectedPayment(opt);
                      }}
                      interface="action-sheet"
                      className="payment-currency-select"
                    >
                      {paymentOptions.map(option => (
                        <IonSelectOption key={option.id} value={option.id}>
                          {option.symbol} {option.price.toFixed(2)} {option.currency} - {option.label}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  </div>
                  <div className="payment-modal-price">
                    <span className="payment-modal-symbol">{selectedPayment.symbol}</span>
                    <span className="payment-modal-amount">{selectedPayment.price.toFixed(2)}</span>
                    <span className="payment-modal-currency">{selectedPayment.currency}</span>
                  </div>
                  <div className="payment-modal-features">
                    <div className="payment-feature">
                      <IonIcon icon={checkmarkCircle} />
                      <span>{t('dashboard.feature_immediate')}</span>
                    </div>
                    <div className="payment-feature">
                      <IonIcon icon={checkmarkCircle} />
                      <span>{t('dashboard.feature_visible')}</span>
                    </div>
                    <div className="payment-feature">
                      <IonIcon icon={checkmarkCircle} />
                      <span>{t('dashboard.feature_reach')}</span>
                    </div>
                  </div>
                  <p className="payment-modal-note">{t('dashboard.payment_note')}</p>
                  <IonButton
                    expand="block"
                    color="success"
                    onClick={handleConfirmPayment}
                    disabled={processingPayment}
                    className="payment-modal-btn"
                  >
                    {processingPayment ? (
                      <>
                        <IonSpinner name="crescent" />
                        <span style={{ marginLeft: '8px' }}>{t('common.processing')}</span>
                      </>
                    ) : (
                      t('dashboard.pay_button', {
                        symbol: selectedPayment.symbol,
                        amount: selectedPayment.price.toFixed(2),
                        currency: selectedPayment.currency
                      })
                    )}
                  </IonButton>
                </div>
              ) : (
                <div className="payment-modal-loading">
                  <p>{t('dashboard.no_payment_options')}</p>
                </div>
              )}
            </div>
          </div>
        )}
        </div>

        {/* FAB Button */}
        <IonFab vertical="bottom" horizontal="end" slot="fixed">
          <IonFabButton onClick={handleCreateEvent}>
            <IonIcon icon={add} />
          </IonFabButton>
        </IonFab>

      </IonContent>

      <ConfirmDialog
        isOpen={showLogoutConfirm}
        title={t('common.logout_confirm_title')}
        message={t('common.logout_confirm_msg')}
        confirmLabel={t('common.logout')}
        cancelLabel={t('common.cancel')}
        confirmVariant="danger"
        onConfirm={handleSignOut}
        onCancel={() => setShowLogoutConfirm(false)}
      />

    </IonPage>
  );
};

/* Event Card Component */
interface EventCardProps {
  event: Event;
  onClick: () => void;
  t: (key: string, params?: Record<string, string>) => string;
}

const EventCard: React.FC<EventCardProps> = ({ event, onClick, t }) => {
  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    return date.toLocaleDateString('es-GT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <IonCard button onClick={onClick} className="event-card">
      <div className="event-card-content">
        <div className="event-thumbnail-col">
          <img src={event.photoItem} alt={event.information} className="event-thumbnail" />
        </div>
        <div className="event-details">
          <IonCardHeader>
            <IonCardTitle>{event.information}</IonCardTitle>
            <IonCardSubtitle>
              📅 {formatDate(event.date)} • {event.schedule_start}
            </IonCardSubtitle>
          </IonCardHeader>
          <IonCardContent>
            <p className="event-address">📍 {event.address_id}</p>
            <div className="event-footer">
              <IonBadge color={getEventStatusColor(event.status, event.published)}>
                {t(getEventStatusLabel(event.status, event.published))}
              </IonBadge>
              {event.price !== undefined && <span className="event-price">{event.currency ?? ''}{Number(event.price).toFixed(2)}</span>}
            </div>
          </IonCardContent>
        </div>
      </div>
    </IonCard>
  );
};

export default DashboardPage;
